import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { formatMoeda } from '../../utils/fmt';
import { resolverPreco, precoVitrine } from '../../utils/precoModelo';
import * as modelosApi from '../../api/modelos';
import * as estoqueApi from '../../api/estoque';
import * as carrinhoApi from '../../api/carrinho';
import * as coresApi from '../../api/cores';
import * as tamanhosApi from '../../api/tamanhos';

const PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="160" viewBox="0 0 200 160"%3E%3Crect width="200" height="160" fill="%23e8eaf0"/%3E%3Ctext x="100" y="85" text-anchor="middle" fill="%23999" font-size="13" font-family="sans-serif"%3ESem foto%3C/text%3E%3C/svg%3E';

function ModalAdicionarCarrinho({ modelo, estoque, cores, tamanhosCatalogo, onClose, onAdded }) {
  const [tamanho, setTamanho] = useState('');
  const [idCor, setIdCor] = useState('');
  const [quantidade, setQuantidade] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  const tamanhos = modelo.tamanhos?.map(t => t.id_tamanho_uniforme) || [];
  const tamanhoNome = id => tamanhosCatalogo.find(t => t.id_tamanho_uniforme === id)?.nome || id;
  const coresDoModelo = (modelo.cores || [])
    .map(mc => cores.find(c => c.id_cor === mc.id_cor))
    .filter(Boolean);
  const precisaCor = modelo.permite_variacao_cor;
  const corPronta = !precisaCor || !!idCor;

  const estoqueItem = estoque.find(e =>
    e.id_modelo_uniforme === modelo.id_modelo_uniforme && e.id_tamanho_uniforme === tamanho &&
    (e.id_cor || null) === (idCor || null)
  );
  const disponivel = estoqueItem?.quantidade_disponivel ?? 0;
  const temEstoque = disponivel >= quantidade;
  const preco = resolverPreco(modelo, tamanho);

  const adicionar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await carrinhoApi.upsertItem({
        id_modelo_uniforme: modelo.id_modelo_uniforme,
        id_tamanho_uniforme: tamanho,
        id_cor: precisaCor ? idCor : null,
        quantidade: parseInt(quantidade),
      });
      setSucesso(true);
      setTimeout(() => { setSucesso(false); onAdded(); }, 1200);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao adicionar ao carrinho.');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">{modelo.nome}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={adicionar}>
            <div className="modal-body">
              {modelo.fotos?.length > 0 && (
                <div style={{ position: 'relative', paddingTop: '75%', overflow: 'hidden', borderRadius: 2, marginBottom: 16 }}>
                  <img src={modelo.fotos[0].url} alt={modelo.nome}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              )}
              {modelo.descricao && (
                <p className="text-muted" style={{ fontSize: 13 }}>{modelo.descricao}</p>
              )}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="fw-bold" style={{ color: 'var(--cjc-primary)', fontSize: 20 }}>
                  {tamanho ? formatMoeda(preco) : (modelo.tipo_preco === 'por_tamanho' ? 'Selecione o tamanho' : formatMoeda(preco))}
                </span>
                <span className="text-muted" style={{ fontSize: 12 }}>por unidade</span>
              </div>

              {precisaCor && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">Cor *</label>
                  <div className="d-flex flex-wrap gap-2">
                    {coresDoModelo.map(c => (
                      <button key={c.id_cor} type="button"
                        className={`btn btn-sm ${idCor === c.id_cor ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setIdCor(c.id_cor)}>
                        {c.codigo_hex && (
                          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: c.codigo_hex, marginRight: 6 }} />
                        )}
                        {c.nome}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label fw-semibold">Tamanho *</label>
                <div className="d-flex flex-wrap gap-2">
                  {tamanhos.map(t => (
                    <button key={t} type="button"
                      className={`btn btn-sm ${tamanho === t ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{ minWidth: 44 }}
                      onClick={() => setTamanho(t)}>
                      {tamanhoNome(t)}
                    </button>
                  ))}
                </div>
              </div>

              {tamanho && corPronta && (
                <div className="mb-3">
                  <div className={`alert py-2 mb-2 ${temEstoque ? 'alert-success' : 'alert-warning'}`} style={{ fontSize: 13 }}>
                    {temEstoque
                      ? `✓ ${disponivel} unidade(s) disponível(is) em estoque`
                      : disponivel > 0
                        ? `Atenção: apenas ${disponivel} em estoque. Quantidade extra vai para fabricação.`
                        : 'Sem estoque disponível — será fabricado sob demanda.'}
                  </div>
                  <label className="form-label fw-semibold">Quantidade *</label>
                  <input type="number" min="1" className="form-control" style={{ maxWidth: 120 }}
                    value={quantidade} onChange={e => setQuantidade(e.target.value)} required />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Fechar</button>
              {sucesso ? (
                <button className="btn btn-success btn-sm" disabled>✓ Adicionado!</button>
              ) : (
                <button type="submit" className="btn btn-primary btn-sm"
                  disabled={!tamanho || !corPronta || loading}>
                  {loading ? <span className="spinner-border spinner-border-sm" /> : 'Adicionar ao Carrinho'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function ModeloCard({ m, disp, tamanhoNome, onClick }) {
  const { valor: precoVit, aPartirDe } = precoVitrine(m);
  return (
    <div className="col-sm-6 col-lg-4 col-xl-3">
      <div className="card h-100" style={{ cursor: 'pointer' }} onClick={onClick}>
        {/* Proporção 4:3 (paddingTop 75%) */}
        <div style={{ position: 'relative', paddingTop: '75%', overflow: 'hidden', borderRadius: '2px 2px 0 0' }}>
          <img
            src={m.fotos?.[0]?.url || PLACEHOLDER}
            alt={m.nome}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
        <div className="card-body d-flex flex-column">
          <h6 className="card-title mb-1" style={{ color: 'var(--cjc-primary)' }}>{m.nome}</h6>
          {m.descricao && (
            <p className="text-muted mb-2" style={{ fontSize: 12, flexGrow: 1,
              overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {m.descricao}
            </p>
          )}
          <div className="d-flex justify-content-between align-items-end mt-auto">
            <span className="fw-bold" style={{ color: 'var(--cjc-primary)', fontSize: 16 }}>
              {aPartirDe && <span className="text-muted" style={{ fontSize: 11, fontWeight: 400 }}>A partir de </span>}
              {formatMoeda(precoVit)}
            </span>
            <span className={`badge ${disp > 0 ? 'bg-success' : 'bg-warning text-dark'}`} style={{ fontSize: 11 }}>
              {disp > 0 ? `${disp} em estoque` : 'Sob demanda'}
            </span>
          </div>
          <div className="d-flex flex-wrap gap-1 mt-2">
            {(m.tamanhos || []).slice(0, 6).map(t => (
              <span key={t.id_tamanho_uniforme} className="badge bg-light text-dark border" style={{ fontSize: 10 }}>
                {tamanhoNome(t.id_tamanho_uniforme)}
              </span>
            ))}
            {(m.tamanhos || []).length > 6 && (
              <span className="badge bg-light text-muted border" style={{ fontSize: 10 }}>
                +{m.tamanhos.length - 6}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Catalogo() {
  const [modelos, setModelos] = useState([]);
  const [estoque, setEstoque] = useState([]);
  const [cores, setCores] = useState([]);
  const [tamanhos, setTamanhos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [modal, setModal] = useState(null);
  const [erroCarregar, setErroCarregar] = useState('');

  const carregar = useCallback(() => {
    setLoading(true);
    setErroCarregar('');
    Promise.all([modelosApi.listar(), estoqueApi.listar(), coresApi.listar(), tamanhosApi.listar()])
      .then(([m, e, c, tam]) => { setModelos(m.data); setEstoque(e.data); setCores(c.data); setTamanhos(tam.data); })
      .catch(() => setErroCarregar('Erro ao carregar dados. Recarregue a página.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const tamanhoNome = id => tamanhos.find(t => t.id_tamanho_uniforme === id)?.nome || id;

  const filtrados = modelos.filter(m =>
    m.nome.toLowerCase().includes(busca.toLowerCase())
  );

  const estoqueDisponivel = (id) => {
    const itens = estoque.filter(e => e.id_modelo_uniforme === id);
    return itens.reduce((s, e) => s + e.quantidade_disponivel, 0);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader titulo="Catálogo" subtitulo={`${modelos.length} modelos disponíveis`} />

      {erroCarregar && <div className="alert alert-danger py-2">{erroCarregar}</div>}

      <div className="mb-4">
        <input className="form-control" style={{ maxWidth: 360 }}
          placeholder="Buscar modelo..." value={busca} onChange={e => setBusca(e.target.value)} />
      </div>

      {filtrados.length === 0 && (
        <div className="text-center text-muted py-5">Nenhum modelo encontrado.</div>
      )}

      <div className="row g-3">
        {filtrados.map(m => (
          <ModeloCard key={m.id_modelo_uniforme} m={m}
            disp={estoqueDisponivel(m.id_modelo_uniforme)}
            tamanhoNome={tamanhoNome}
            onClick={() => setModal(m)} />
        ))}
      </div>

      {modal && (
        <ModalAdicionarCarrinho
          modelo={modal}
          estoque={estoque}
          cores={cores}
          tamanhosCatalogo={tamanhos}
          onClose={() => setModal(null)}
          onAdded={() => setModal(null)}
        />
      )}
    </div>
  );
}
